/**
 * createShellColumns — Unified column set für die Redesign-Shell (S6)
 * Strategie: createMergedColumns (Plan-012: readiness_action, packshot, readiness_score,
 * issues_count) PLUS performance_30d Sparkline + quality_score aus dem S1-Redesign.
 * Kommentar: Durchgängig mergedProductColumns-basiert (ProductWithReadiness);
 * performance_30d und quality_score werden additiv eingefügt (nach revenue_30d).
 */
import { createColumnHelper } from '@tanstack/react-table'
import type { TFunction } from 'i18next'
import { Sparkline } from '@/components/v2/ui/Sparkline'
import { createMergedColumns } from './mergedProductColumns'
import type { ProductWithReadiness } from '@/types/products'

const col = createColumnHelper<ProductWithReadiness>()

/** S1 Sparkline column (performance_30d) typed for ProductWithReadiness */
function makeSparklineColumn() {
  return col.display({
    id: 'performance_30d',
    header: 'Performance 30T',
    enableSorting: false,
    cell: ({ row, table }) => {
      const meta = table.options.meta as
        | { seriesByProduct?: Record<string, { sale_date: string; revenue: number }[]> }
        | undefined
      const pts = (meta?.seriesByProduct?.[row.original.id] ?? []).map(d => ({ x: d.sale_date, y: d.revenue }))
      return (
        <div className="flex items-center gap-2">
          <span className="tabular-nums text-xs">
            {(row.original.revenue_30d ?? 0).toLocaleString('de-DE', { maximumFractionDigits: 0 })} €
          </span>
          <Sparkline data={pts} />
        </div>
      )
    },
  })
}

/** S1 quality_score column typed for ProductWithReadiness (reads readiness_score) */
function makeQualityScoreColumn() {
  return col.display({
    id: 'quality_score',
    header: 'Quality Score',
    enableSorting: true,
    cell: ({ row }) => {
      const s = row.original.readiness_score
      if (s == null) return <span className="text-neutral-400">–</span>
      const color = s >= 70 ? 'text-emerald-600' : s >= 40 ? 'text-amber-500' : 'text-red-600'
      return <span className={`font-semibold ${color}`}>{s}</span>
    },
  })
}

export function createShellColumns(
  t: TFunction,
  onBadgeClick: (product: ProductWithReadiness) => void,
  onPackshotAssign: (productId: string) => void,
  onOpenQuickView?: (product: ProductWithReadiness) => void,
  onGenerateImage?: (product: ProductWithReadiness) => void,
) {
  const merged = createMergedColumns(t, onBadgeClick, onPackshotAssign, onOpenQuickView, onGenerateImage)
  // Insert performance_30d + quality_score after revenue_30d (index ~5)
  const revenueIdx = merged.findIndex(c => (c as { accessorKey?: string }).accessorKey === 'revenue_30d')
  const insertAt = revenueIdx >= 0 ? revenueIdx + 1 : merged.length - 1
  return [
    ...merged.slice(0, insertAt),
    makeSparklineColumn(),
    makeQualityScoreColumn(),
    ...merged.slice(insertAt),
  ]
}
